/* ==========================================================================
   crtz-pdp-mobile.js

   Product page, phones only: put SIZE and COLOUR on one row, and turn the
   copy below Add to Cart into accordions.

   Draft theme GENERAL RELEASE - July 24 (LL), id 162945400962.
   September 2026. Pairs with crtz-pdp-mobile.css, which holds all the
   styling -- this file only moves and wraps nodes.

   --------------------------------------------------------------------------
   WHY JS AT ALL, AND WHY SO LITTLE OF IT

   Two things CSS cannot do here:

   1. SIZE AND COLOUR ARE IN DIFFERENT PARENTS. The theme emits

        div
          p.product-single__prices
          div.color-group          <- colour, outside the form
          form.product-form
            div.variant-group      <- size, inside the form

      To put them on one row they have to be siblings. `form { display:
      contents }` would have done it without JS, but display:contents on a
      form element has a history of dropping the form from the accessibility
      tree, and this is the add-to-cart form. Moving one node is the smaller
      risk.

   2. ACCORDIONS NEED A HEADER ELEMENT that does not exist in the markup.

   Everything else -- every dimension, colour and breakpoint -- is in the CSS.

   --------------------------------------------------------------------------
   WHY IT RUNS AT EVERY WIDTH

   The DOM changes below are made once, at any viewport, and the CSS decides
   whether they mean anything. That is deliberate:

     - Rebuilding on resize means tearing down and re-inserting the add-to-cart
       form's neighbours while a customer may be mid-interaction.
     - The theme's own JS binds to .variant-square and .color-square on load.
       Moving their containers once, before those handlers matter, is safe.
       Moving them repeatedly is asking for a stale reference.

   Above 749px the CSS applies nothing, and the wrappers are inert divs. This
   was checked on desktop: the page is unchanged.

   --------------------------------------------------------------------------
   WHAT IT DELIBERATELY DOES NOT TOUCH

   - The form, its action, its inputs, or #SelectedVariantId.
   - .variant-square / .color-square elements themselves, or their handlers.
   - The .size-lightbox element. Its content is MOVED into a panel, but the
     element stays in the DOM, because theme.js binds featherlight to it and
     a missing element would throw on desktop.

   --------------------------------------------------------------------------
   FAILURE MODE

   Everything is inside one try/catch. If anything throws, the page is left
   exactly as the theme rendered it -- stacked size/colour and an expanded
   description. That is the current live layout, so a failure here degrades
   to "no improvement", never to a broken buy button.
   ========================================================================== */

(function () {
  'use strict';

  /* Used only when data-crtz-summary is absent. See the shipping block below. */
  var FALLBACK_SUMMARY = [
    'UK DOMESTIC ORDERS SHIP WITHIN 5-10 WORKING DAYS',
    'INTERNATIONAL ORDERS 5-15 WORKING DAYS',
    'UNLESS A PRE-ORDER SHIP DATE IS GIVEN'
  ].join('|');

  function text(el) {
    return (el.textContent || '').replace(/\s+/g, ' ').trim();
  }

  /* Build one accordion item. `nodes` are moved into the panel. */
  function makeItem(label, nodes, startOpen) {
    var item = document.createElement('div');
    item.className = 'crtz-acc__item';

    var head = document.createElement('button');
    head.type = 'button';                 /* not "submit" -- it lives near a form */
    head.className = 'crtz-acc__head';
    head.textContent = label;
    head.setAttribute('aria-expanded', startOpen ? 'true' : 'false');

    var panel = document.createElement('div');
    panel.className = 'crtz-acc__panel';
    panel.hidden = !startOpen;

    for (var i = 0; i < nodes.length; i++) panel.appendChild(nodes[i]);

    head.addEventListener('click', function () {
      var open = head.getAttribute('aria-expanded') === 'true';
      head.setAttribute('aria-expanded', open ? 'false' : 'true');
      panel.hidden = open;
    });

    item.appendChild(head);
    item.appendChild(panel);
    return item;
  }

  function init() {
    var meta = document.querySelector('.product-single__meta');
    if (!meta || meta.getAttribute('data-crtz-pdp') === 'done') return;

    /* ---- 1. SIZE + COLOUR on one row ---------------------------------- */
    var variantGroup = meta.querySelector('.variant-group');
    var colorGroup = meta.querySelector('.color-group');

    if (variantGroup && colorGroup && variantGroup.parentNode) {
      var row = document.createElement('div');
      row.className = 'crtz-pdp-optionrow';
      variantGroup.parentNode.insertBefore(row, variantGroup);
      row.appendChild(variantGroup);
      row.appendChild(colorGroup);       /* moves it across the form boundary */
    }

    /* ---- 2. Accordions, EVERY WIDTH -----------------------------------
       This ran phones-only for a few hours on 6 September 2026 and it was
       wrong. The history matters, because both failures look like different
       bugs and are the same one:

         - Built at every width while its CSS was phone-only, desktop showed
           bare unstyled buttons, no copy (collapsed panel), and the stock
           SIZE GUIDE / SHIPPING POLICY list still above it -- every row
           twice.
         - Guarded to phones only, desktop lost the dropdowns altogether,
           which is not what was wanted. It also left a nasty trap on
           mobile: the guard is evaluated ONCE, here, at DOMContentLoaded, so
           a page opened on a wide window and then narrowed never built an
           accordion at all -- the rows simply were not there, at a width
           where they should have been.

       Both are gone now. The accordion is built at every width and
       `.crtz-acc__*` is styled at every width, and `.product-guides` is
       hidden at every width so nothing is listed twice. Those three live or
       die together -- if one ever goes back behind a breakpoint, the others
       have to follow.
       ------------------------------------------------------------------ */

    var desc = document.querySelector('.product-single__description');
    if (!desc) { meta.setAttribute('data-crtz-pdp', 'done'); return; }

    var guides = desc.querySelector('.product-guides');

    /* Everything before .product-guides is the product copy. It is authored
       per product in the description field, so its shape varies -- some
       products have one ul, some have extra paragraphs. Taking "all nodes
       before the guides list" is the only rule that holds for all of them. */
    var detailNodes = [];
    var n = desc.firstChild;
    while (n && n !== guides) {
      var next = n.nextSibling;
      /* skip whitespace-only text nodes so the panel does not open with a gap */
      if (n.nodeType !== 3 || text(n)) detailNodes.push(n);
      n = next;
    }

    var acc = document.createElement('div');
    acc.className = 'crtz-acc';

    if (detailNodes.length) {
      /* Closed by default, like every other row. The reference mockup shows
         it expanded, but that was a mockup of the panel's contents rather
         than of the landing state -- an accordion that is already open on
         page load is not an accordion, and it puts the copy back between the
         customer and the rest of the page. Lewis asked for closed on
         2 September 2026. Pass `true` here to reverse it. */
      acc.appendChild(makeItem('Details', detailNodes, false));
    }

    if (guides) {
      var links = guides.querySelectorAll('a');
      for (var i = 0; i < links.length; i++) {
        var a = links[i];
        var label = text(a);
        if (!label) continue;

        /* The size guide's content is the .size-lightbox div that sits
           beside the link. Move its CHILDREN into a panel and leave the
           empty .size-lightbox where it is -- theme.js binds featherlight to
           that element and removing it would throw on desktop. */
        var box = guides.querySelector('.size-lightbox');
        if (box && /size\s*guide/i.test(label)) {
          var boxNodes = [];
          while (box.firstChild) boxNodes.push(box.removeChild(box.firstChild));
          acc.appendChild(makeItem(label, boxNodes, false));
          continue;
        }

        /* Shipping policy. The stock markup is a bare link that navigates
           away, so show a summary in the panel with the link below it. The
           text comes from data-crtz-summary, which product-description.liquid
           fills from the real policy. */
        var href = a.getAttribute('href');
        if (href) {
          var lines = (a.getAttribute('data-crtz-summary') || FALLBACK_SUMMARY)
            .split('|')
            .map(function (s) { return s.trim(); })
            .filter(Boolean);

          var summary = document.createElement('p');
          for (var j = 0; j < lines.length; j++) {
            if (j) summary.appendChild(document.createElement('br'));
            summary.appendChild(document.createTextNode(lines[j]));
          }

          var more = document.createElement('p');
          var moreLink = document.createElement('a');
          moreLink.href = href;
          moreLink.textContent = 'CLICK HERE TO LEARN MORE';
          more.appendChild(moreLink);

          acc.appendChild(makeItem(label, [summary, more], false));
        }
      }
    }

    if (acc.children.length) desc.appendChild(acc);

    meta.setAttribute('data-crtz-pdp', 'done');
  }

  function boot() {
    try { init(); } catch (e) {
      /* Leave the page as the theme rendered it. */
      if (window.console && console.warn) console.warn('crtz-pdp-mobile:', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
